import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProject, getTasks, getTimeEntries, getTimeByUser, createTask, getNotes, moveTask } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatTime, formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { ArrowLeft, Plus, CheckSquare, Clock, User, DollarSign, TrendingUp, Receipt, StickyNote, Calendar, ChevronRight, Target, Play } from 'lucide-react';
import { useTimer } from '../lib/TimerContext';
import { toast } from 'sonner';
import TaskDetailDialog from '../components/TaskDetailDialog';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const COLORS = ['#006CB6', '#F4E600', '#000000', '#808080', '#E5E5E5'];

const STATUS_FLOW = {
  todo: { next: 'in_progress', label: 'Rozpracovat' },
  in_progress: { next: 'done', label: 'Dokončit' },
  done: { prev: 'in_progress', label: 'Vrátit' },
};

const ProjectDetail = () => {
  const { id } = useParams();
  const { hasPermission, user } = useAuth();
  const { isRunning, startTimer } = useTimer();
  const isAdmin = user?.is_admin;
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [userTimeData, setUserTimeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'long_term',
    estimated_minutes: '',
  });

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setDetailDialogOpen(true);
  };

  const handleNextStage = async (task, e) => {
    e.stopPropagation();
    const flow = STATUS_FLOW[task.status];
    if (!flow) return;
    
    const newStatus = flow.next || flow.prev;
    if (!newStatus) return;
    
    try {
      await moveTask(task.id, { new_status: newStatus, new_order: 0 });
      toast.success(`Úkol přesunut`);
      fetchData();
    } catch (error) {
      toast.error('Chyba při přesunu úkolu');
    }
  };

  const fetchData = async () => {
    try {
      const [projectRes, tasksRes, notesRes] = await Promise.all([
        getProject(id),
        getTasks({ project_id: id }),
        getNotes({ project_id: id }),
      ]);
      setProject(projectRes.data);
      setTasks(tasksRes.data);
      setNotes(notesRes.data);

      if (hasPermission('time_tracking')) {
        const userTimeRes = await getTimeByUser(id);
        setUserTimeData(userTimeRes.data);
      }
    } catch (error) {
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleSubmitTask = async (e) => {
    e.preventDefault();
    try {
      await createTask({
        ...formData,
        project_id: id,
        status: 'todo',
        estimated_minutes: formData.estimated_minutes ? parseInt(formData.estimated_minutes) : null,
      });
      toast.success('Úkol vytvořen');
      setDialogOpen(false);
      setFormData({ title: '', description: '', priority: 'long_term', estimated_minutes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při vytváření úkolu');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'todo':
        return 'bg-gray-100 border-gray-300';
      case 'in_progress':
        return 'bg-blue-100 border-blue-300';
      case 'done':
        return 'bg-green-100 border-green-300';
      default:
        return 'bg-gray-100 border-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'todo': return 'K řešení';
      case 'in_progress': return 'Rozpracováno';
      case 'done': return 'Hotovo';
      default: return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const tasksByStatus = {
    todo: tasks.filter((t) => t.status === 'todo'),
    in_progress: tasks.filter((t) => t.status === 'in_progress'),
    done: tasks.filter((t) => t.status === 'done'),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-oswald text-xl uppercase tracking-wide">Načítání...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <div className="font-oswald text-xl uppercase">Projekt nenalezen</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-detail">
      {/* Header */}
      <div>
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 font-oswald uppercase text-sm tracking-wide text-gray-600 hover:text-black mb-4"
        >
          <ArrowLeft size={16} />
          Zpět na projekty
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">
              {project.name}
            </h1>
            <Link to={`/clients/${project.client_id}`} className="font-serif text-gray-600 hover:text-[#006CB6]">
              {project.client_name}
            </Link>
          </div>
          <button
            onClick={() => startTimer({ project_id: project.id, project_name: project.name })}
            disabled={isRunning}
            title={isRunning ? 'Stopky již běží' : 'Spustit stopky'}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#F4E600] text-black font-oswald uppercase text-sm tracking-wide border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
            data-testid="start-timer-btn"
          >
            <Play size={16} />
            Spustit stopky
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={16} className="text-gray-500" />
            <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">
              Celkový čas
            </div>
          </div>
          <div className="font-oswald text-3xl font-bold">{formatTime(project.total_time_minutes)}</div>
        </div>
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-2 mb-1">
            <CheckSquare size={16} className="text-gray-500" />
            <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">
              Úkoly
            </div>
          </div>
          <div className="font-oswald text-3xl font-bold">
            {tasksByStatus.done.length}/{tasks.length}
          </div>
          <div className="text-xs text-gray-500 font-serif mt-1">dokončeno</div>
        </div>
        {hasPermission('revenue') && (
          <>
            <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp size={16} className="text-gray-500" />
                <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">
                  Tržby
                </div>
              </div>
              <div className="font-oswald text-2xl font-bold">
                {project.actual_revenue ? formatCurrency(project.actual_revenue) : '-'}
              </div>
              <div className="text-xs text-gray-500 font-serif mt-1">
                Odhad: {project.estimated_revenue ? formatCurrency(project.estimated_revenue) : '-'}
              </div>
            </div>
            <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <div className="flex items-center gap-2 mb-1">
                <Receipt size={16} className="text-gray-500" />
                <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">
                  Náklady / Zisk
                </div>
              </div>
              <div className="font-oswald text-2xl font-bold text-red-600">
                {formatCurrency(project.total_cost || 0)}
              </div>
              {project.actual_revenue && (
                <div className={`text-xs font-serif mt-1 ${(project.actual_revenue - (project.total_cost || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Zisk: {formatCurrency(project.actual_revenue - (project.total_cost || 0))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Project Info & Capacity */}
      <div className="bg-white border-2 border-gray-200 p-4 space-y-4">
        {(project.start_date || project.end_date) && (
          <div className="flex gap-6">
            {project.start_date && (
              <div>
                <div className="font-oswald text-xs uppercase text-gray-500">Datum začátku</div>
                <div className="font-serif">{project.start_date}</div>
              </div>
            )}
            {project.end_date && (
              <div>
                <div className="font-oswald text-xs uppercase text-gray-500">Datum konce</div>
                <div className="font-serif">{project.end_date}</div>
              </div>
            )}
          </div>
        )}
        
        {/* Capacity Progress Bar */}
        {project.max_hours && project.max_hours > 0 && (
          <div className="pt-2 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="font-oswald text-xs uppercase text-gray-500">Kapacita projektu</div>
              <div className="font-serif text-sm">
                {(project.used_hours || 0).toFixed(1)}h / {project.max_hours}h
              </div>
            </div>
            <Progress 
              value={Math.min(((project.used_hours || 0) / project.max_hours) * 100, 100)} 
              className="h-3"
            />
            <div className="text-xs text-gray-500 font-serif mt-1">
              {((project.used_hours || 0) / project.max_hours * 100).toFixed(0)}% využito
              {((project.used_hours || 0) / project.max_hours) > 1 && (
                <span className="text-red-600 font-bold ml-2">⚠ Kapacita překročena!</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Notes Section */}
      {notes.length > 0 && (
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
            <StickyNote size={20} />
            Poznámky ({notes.length})
          </h2>
          <div className="space-y-4">
            {notes.map((note) => (
              <div key={note.id} className="border-2 border-gray-200 p-4 hover:border-black transition-colors">
                <div className="flex items-start justify-between">
                  <h3 className="font-oswald font-bold">{note.title}</h3>
                  {note.meeting_date && (
                    <span className="text-xs text-gray-500 font-serif flex items-center gap-1">
                      <Calendar size={12} />
                      {note.meeting_date}
                    </span>
                  )}
                </div>
                <p className="font-serif text-sm text-gray-600 mt-2 whitespace-pre-wrap line-clamp-3">
                  {note.content}
                </p>
                <div className="text-xs text-gray-400 font-serif mt-2">
                  {note.created_by_name} • {formatDate(note.created_at)}
                </div>
              </div>
            ))}
          </div>
          <Link 
            to="/notes" 
            className="inline-block mt-4 font-oswald text-sm uppercase text-[#006CB6] hover:underline"
          >
            Zobrazit všechny poznámky →
          </Link>
        </div>
      )}

      {/* Time by User Chart */}
      {hasPermission('time_tracking') && userTimeData.length > 0 && (
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">
            Čas podle uživatelů
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={userTimeData} layout="vertical">
                <XAxis type="number" tickFormatter={(v) => formatTime(v)} />
                <YAxis type="category" dataKey="user_name" width={100} tick={{ fontFamily: 'Oswald', fontSize: 12 }} />
                <Tooltip formatter={(value) => formatTime(value)} />
                <Bar dataKey="total_minutes" fill="#006CB6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tasks Board */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-oswald text-xl font-bold uppercase tracking-tight">Úkoly</h2>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-black text-white font-oswald uppercase tracking-wide border-2 border-black rounded-none hover:bg-gray-900 transition-all"
                data-testid="add-task-btn"
              >
                <Plus size={18} className="mr-2" />
                Nový úkol
              </Button>
            </DialogTrigger>
            <DialogContent className="border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <DialogHeader>
                <DialogTitle className="font-oswald text-xl uppercase tracking-tight">
                  Nový úkol
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmitTask} className="space-y-4">
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Název</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    data-testid="task-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Popis</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    data-testid="task-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
                    <Target size={12} /> Odhadovaný čas (min)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.estimated_minutes}
                    onChange={(e) => setFormData({ ...formData, estimated_minutes: e.target.value })}
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    placeholder="např. 120"
                    data-testid="task-estimated-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                  data-testid="task-submit-btn"
                >
                  Vytvořit úkol
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Kanban Board */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { key: 'todo', label: 'K udělání', color: '#808080' },
            { key: 'in_progress', label: 'Rozpracováno', color: '#006CB6' },
            { key: 'done', label: 'Dokončeno', color: '#22c55e' },
          ].map((column) => (
            <div key={column.key} className="border-2 border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: column.color }} />
                <h3 className="font-oswald text-sm uppercase tracking-wide">{column.label}</h3>
                <span className="ml-auto font-oswald text-sm">{tasksByStatus[column.key].length}</span>
              </div>
              <div className="space-y-3">
                {tasksByStatus[column.key].map((task) => {
                  const flow = STATUS_FLOW[task.status];
                  return (
                    <div
                      key={task.id}
                      onClick={() => handleTaskClick(task)}
                      className={`p-3 border-2 ${getStatusColor(task.status)} hover:border-black transition-colors cursor-pointer`}
                      data-testid={`task-${task.id}`}
                    >
                      <div className="font-serif font-medium text-sm">{task.title}</div>
                      <div className="flex items-center justify-between mt-2 text-xs">
                        <span className={`font-oswald uppercase ${task.priority === 'urgent' ? 'text-red-600' : 'text-gray-600'}`}>
                          {task.priority === 'urgent' ? 'Urgentní' : 'Dlouhodobý'}
                        </span>
                        <div className="flex items-center gap-2">
                          {task.estimated_minutes > 0 && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <Target size={10} />
                              {formatTime(task.estimated_minutes)}
                            </span>
                          )}
                          <span className="font-oswald flex items-center gap-1">
                            <Clock size={10} />
                            {formatTime(task.total_time_minutes)}
                          </span>
                        </div>
                      </div>
                      {/* Next Stage Button */}
                      {flow && (
                        <button
                          onClick={(e) => handleNextStage(task, e)}
                          className="mt-2 w-full flex items-center justify-center gap-1 text-xs font-oswald uppercase px-2 py-1 border border-[#006CB6] text-[#006CB6] hover:bg-[#006CB6] hover:text-white transition-colors"
                          data-testid={`task-next-stage-${task.id}`}
                        >
                          <ChevronRight size={12} />
                          {flow.label}
                        </button>
                      )}
                    </div>
                  );
                })}
                {tasksByStatus[column.key].length === 0 && (
                  <p className="text-center text-gray-400 text-sm py-4">Žádné úkoly</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Task Detail Dialog */}
      <TaskDetailDialog
        task={selectedTask}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        onTaskUpdated={fetchData}
      />
    </div>
  );
};

export default ProjectDetail;
